import { Col, Form, Input, InputNumber, Modal, Row, Switch, notification } from 'antd';
import React, { useState } from 'react';

import MarketingCampaignSelect from '../inputs/MarketingCampaignSelect';
import MarketingTierSelect from '../inputs/VendorCodeMarketingTierSelect';
import PhoneInput from '../../core/PhoneInput';
import ROUTES from '../../../constants/routes';
import RequestErrorAlert from '../../shared/RequestErrorAlert';
import type { TVendorCodesInternetHomeWarranty } from './../../../models/vendor/VendorCodeModels';
import VendorCodeSecretInput from '../inputs/VendorCodeSecretInput';
import VendorInput from '../inputs/VendorInput';
import useCreateVendorCodeInternetHomeWarranty from './../../../hooks/services/vendor/useCreateVendorCodeInternetHomeWarranty';
import { useNavigate } from 'react-router-dom';
import useUpdateVendorCodeInternetHomeWarranty from './../../../hooks/services/vendor/useUpdateVendorCodeInternetHomeWarranty';
import useVendors from '../../../hooks/services/vendor/useVendors';
import { validatePhoneNumber } from '../../../utils/validators';

const FORM_LAYOUT = {
  labelCol: { span: 12 },
  wrapperCol: { span: 10 },
};

const REQUIRED_RULE = { message: 'This field is required', required: true };

const PHONE_RULE = {
  validator: (rule: any, value: string) => {
    return !value || validatePhoneNumber(value)
      ? Promise.resolve()
      : Promise.reject(new Error('Enter a valid phone number.'));
  },
};

type Props = {
  initialData?: TVendorCodesInternetHomeWarranty;
  isShown: boolean;
  setIsShown: (isShown: boolean) => void;
  vendorId?: number;
};

const DEFAULT_VENDOR_CODE_INTERNET_HOME_WARRANTY = {
  isActive: true,
  name: '',
  vendorId: '',
  secret: '',
  duplicateDays: '',
  maxHomeAge: '',
  marketingTier: 1,
  dialerCampaignId: 0,
  autoSmsOptIn: true,
  isEmailOnly: false,
  isPreflightOnly: false,
  sendToAIAgent: false,
  legacyVendorId: '',
  tollFreeNumber: '',
};

const VendorCodeInternetHomeWarrantyModalForm = ({
  initialData,
  isShown,
  setIsShown,
  vendorId,
}: Props) => {
  const { create, isLoading: isCreating } = useCreateVendorCodeInternetHomeWarranty();
  const { update, isLoading: isUpdating } = useUpdateVendorCodeInternetHomeWarranty();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const isLoading = isCreating || isUpdating;
  const isNewVendorCodeInternetHomeWarranty = !initialData;
  const { data } = useVendors();
  const [selectedVendorId, setSelectedVendorId] = useState<any>(vendorId);
  const [selectedVendorShortName, setSelectedVendorShortName] = useState<any>('');
  const [vendorCode, setVendorCode] = useState<any>('');
  const [errorMessage, setErrorMessage] = useState('');
  const [errorOccured, setErrorOccured] = useState<boolean>(false);

  const onCancel = React.useCallback(() => {
    setErrorOccured(false);
    form.resetFields();
    const shortName =
      data?.filter((x) => x.id === selectedVendorId).map((x) => x.shortName)[0] ?? '';
    setSelectedVendorShortName(shortName);
    setSelectedVendorId(vendorId);
    setVendorCode('');
    setIsShown(false);
  }, [data, form, selectedVendorId, setIsShown, vendorId]);

  const onUpdate = React.useCallback(() => {
    form.resetFields();
    setIsShown(false);
    notification.success({
      message: <strong>Successfully Updated</strong>,
      description: <span>Internet home warranty vendor code successfully updated.</span>,
      placement: 'bottomLeft',
    });
  }, [form, setIsShown]);

  const navigateToDetailPage = React.useCallback(
    (data?: TVendorCodesInternetHomeWarranty) => {
      if (!data?.id) {
        form.resetFields();
        setIsShown(false);
        notification.success({
          message: <strong>Successfully Created</strong>,
          description: <span>Internet home warranty vendor code successfully created.</span>,
          placement: 'bottomLeft',
        });
        return;
      }

      navigate(`${ROUTES.VENDOR_CODES_INTERNET_HOME_WARRANTY}/${data.id}`, {
        state: { isNew: true, name: data.name },
      });
    },
    [form, navigate, setIsShown],
  );

  const onSubmit = React.useCallback(async () => {
    await form.validateFields();
    if (isNewVendorCodeInternetHomeWarranty) {
      create(form.getFieldsValue(), {
        onSuccess: (data) => navigateToDetailPage(data),
        onError: (error) => {
          setErrorOccured(true);
          setErrorMessage(
            error?.response?.data?.message ??
              'Unable to create internet home warranty vendor code.',
          );
        },
      });
      return;
    }

    update(
      {
        id: initialData?.id,
        moxyCustomerNumberPrefix: initialData?.moxyCustomerNumberPrefix,
        ...form.getFieldsValue(),
      },
      {
        onSuccess: onUpdate,
        onError: (error) => {
          setErrorOccured(true);
          setErrorMessage(
            error?.response?.data?.message ??
              'Unable to update internet home warranty vendor code.',
          );
        },
      },
    );
  }, [
    initialData?.id,
    initialData?.moxyCustomerNumberPrefix,
    isNewVendorCodeInternetHomeWarranty,
    form,
    create,
    navigateToDetailPage,
    onUpdate,
    update,
  ]);

  React.useEffect(() => {
    form.resetFields();
  }, [initialData, form]);

  React.useEffect(() => {
    if (isNewVendorCodeInternetHomeWarranty) {
      if (selectedVendorShortName || vendorCode) {
        form.setFieldValue('name', selectedVendorShortName + '-INT-HW-' + vendorCode);
      } else {
        form.setFieldValue('name', '');
      }
    }
  }, [isNewVendorCodeInternetHomeWarranty, selectedVendorShortName, vendorCode, form]);

  React.useEffect(() => {
    const shortName =
      data?.filter((x) => x.id === selectedVendorId).map((x) => x.shortName)[0] ?? '';
    setSelectedVendorShortName(shortName);
  }, [selectedVendorId, data]);

  function handleVendorSelected(vendorId: any) {
    setSelectedVendorId(vendorId);
  }

  const [_, setMarketingTier] = useState(0);
  const handleMarketingTier = (value) => {
    setMarketingTier(value);
  };

  return (
    <Modal
      okButtonProps={{ loading: isLoading }}
      onOk={onSubmit}
      onCancel={onCancel}
      title={`${
        isNewVendorCodeInternetHomeWarranty ? 'Create' : 'Update'
      } Vendor Code - Internet Home Warranty`}
      open={isShown}
      width={900}
      okText={isNewVendorCodeInternetHomeWarranty ? 'Create' : 'Update'}
    >
      <Form
        {...FORM_LAYOUT}
        form={form}
        initialValues={
          initialData ?? {
            ...DEFAULT_VENDOR_CODE_INTERNET_HOME_WARRANTY,
            vendorId: vendorId ?? '',
          }
        }
      >
        <RequestErrorAlert isError={errorOccured} description={errorMessage} />
        <Row>
          <Col span={12}>
            <Form.Item label="Code" name="legacyVendorId" rules={[REQUIRED_RULE]}>
              <Input
                maxLength={64}
                disabled={!!initialData?.legacyVendorId}
                onChange={(value) => setVendorCode(value.target.value)}
              />
            </Form.Item>
            <Form.Item label="Vendor" name="vendorId" rules={[REQUIRED_RULE]}>
              <VendorInput
                disabled={!!vendorId || !isNewVendorCodeInternetHomeWarranty}
                onChange={handleVendorSelected}
              />
            </Form.Item>
            <Form.Item label="API Username" name="name" rules={[REQUIRED_RULE]}>
              <Input maxLength={50} disabled />
            </Form.Item>
            <Form.Item label="API Secret" name="secret" rules={[REQUIRED_RULE]}>
              <VendorCodeSecretInput secretLength={16} value={'initialData?.secret'} />
            </Form.Item>
            <Form.Item label="Campaign" name="marketingCampaignId" rules={[REQUIRED_RULE]}>
              <MarketingCampaignSelect />
            </Form.Item>
            <Form.Item
              label="Toll Free Number"
              name="tollFreeNumber"
              validateFirst={true}
              validateTrigger="onOk"
              rules={[PHONE_RULE]}
            >
              <PhoneInput />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Duplicate Days" name="duplicateDays" rules={[REQUIRED_RULE]}>
              <InputNumber min={0} />
            </Form.Item>
            <Form.Item label="Dialer Campaign Id" name="dialerCampaignId">
              <InputNumber min={0} />
            </Form.Item>
            <Form.Item label="Max Home Age" name="maxHomeAge" rules={[REQUIRED_RULE]}>
              <InputNumber min={0} />
            </Form.Item>
            <Form.Item label="Tier" name="marketingTier" rules={[REQUIRED_RULE]}>
              <MarketingTierSelect
                value={initialData?.marketingTier}
                onChange={handleMarketingTier}
              />
            </Form.Item>
            <Form.Item label="Allow SMS" name="autoSmsOptIn" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item label="Email Only" name="isEmailOnly" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item label="Preflight Only" name="isPreflightOnly" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item label="Send To AI Agent" name="sendToAIAgent" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item label="Active" name="isActive" valuePropName="checked">
              <Switch disabled={isNewVendorCodeInternetHomeWarranty} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default VendorCodeInternetHomeWarrantyModalForm;
